<template>
  <maps-main
    :centre="centre"
    :search-filters="mapSearchFilters"
    :visible-marker="visibleMarker"
    :data-lang="lang"
    @search-filters-updated="updateSearch"
    @marker-clicked="$emit('marker-clicked', $event)"
  >
    <nuxt-to-meta :nuxt2meta="head" />
  </maps-main>
</template>

<script lang="ts">
import { Component, Prop, toNative, Vue } from '~/utility/pu-class-decorator'
import { logger } from '../../utility/logger'
import { SearchFilters } from '../search/schemas'
import { SearchLinkGenerator } from '../url/generators/SearchLinkGenerator'
import { SearchFiltersUrl } from '../url/search-filters/SearchFiltersUrl'

const isDev = process.env.NODE_ENV !== 'production'

@Component({
  emits: ['mapMarkerOpened', 'marker-clicked'],
  setup() {
    const searchStore = useSearchStore()
    return { searchStore }
  },
})
class MapsPage extends Vue {
  get lang() {
    return this.$route.params.lang || 'en-gb'
  }

  @Prop({ required: false })
  hoveredCampsite?: string

  get head() {
    if (this.$isDesktop) {
      return {}
    }
    return {
      meta: [
        {
          hid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  }

  async updateSearch(searchFilters: SearchFilters) {
    logger('updateSearch', searchFilters)

    /* eslint-disable @typescript-eslint/no-unsafe-call -- Route name includes() call is safe */
    const path = this.$isDesktop
      ? this.$route.name?.includes('searchold')
        ? '/searchold/'
        : this.$route.name?.includes('searchnew')
          ? '/searchnew/'
          : '/search/'
      : '/maps/'
    /* eslint-enable @typescript-eslint/no-unsafe-call */
    const newUrl = this.$puLocalePath({
      path,
      query: SearchFiltersUrl.filtersToUrl(searchFilters).query as Record<
        string,
        string | string[]
      >,
    })
    if (newUrl === window.location.pathname + window.location.search) {
      return
    }
    if (isDev) {
      logger('MapsPage.updateSearch', newUrl, searchFilters)
    }
    await this.$router.push(newUrl)
  }

  async mounted() {
    if (isDev) {
      logger('MapsPage.mounted', this.$route.fullPath)
    }
    await this.handleBoundsAndHierarchyUrl()
  }

  destroyed() {
    if (isDev) {
      logger('MapsPage.destroyed', this.$route.fullPath)
    }
  }

  private async handleBoundsAndHierarchyUrl() {
    // if we have bounds and path remove the bounds
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- SearchFilters type needs refinement for bounds/hierarchyPath
    if (this.mapSearchFilters.bounds && this.mapSearchFilters.hierarchyPath) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Destructuring untyped properties
      const {
        bounds: _bounds,
        hierarchyPath,
        ...filters
      } = this.mapSearchFilters
      const linkGenerator = new SearchLinkGenerator()

      /* eslint-disable @typescript-eslint/no-unsafe-assignment -- Destructured values from untyped searchFilters */
      const urlData = linkGenerator.getUrlData({
        category: 'campsites',
        hierarchyPath,
        filters,
      })
      /* eslint-enable @typescript-eslint/no-unsafe-assignment */
      await this.$router.push({
        path: urlData.path,
        query: urlData.query as Record<string, string | string[]>,
      })
    }
  }

  get mapSearchFilters() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- SearchStore typing needs improvement
    return this.searchStore.searchFilters
  }

  get visibleMarker() {
    return this.$route.query.visibleMarker || this.hoveredCampsite
  }

  get centre() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- i18n localeInfo type needs refinement
    return this.$route.query.centre || this.$i18n.localeInfo.mapCenter
  }
}

export default toNative(MapsPage)
</script>
